/** §5.8 — hold the grid's first VISIBLE frame until the resolved saved view has been applied.
 *
 * A leaf module (no AG, no component) so the reveal logic is testable without an AG-in-jsdom harness.
 */
import { useCallback, useEffect, useState } from 'react';

/**
 * Failsafe for the hold. A shuffling grid is a cosmetic defect; a permanently blank one is not, so
 * the hold always ends — even if a precondition we haven't thought of never converges.
 */
export const FIRST_FRAME_HOLD_MS = 3000;

/**
 * `[held, markApplied]` — whether the grid should still be invisible, and the callback the
 * default-view apply calls once the resolved layout is on the columns.
 *
 * ⚠ The grid must still MOUNT while held (opacity, not unmount): AG only accepts
 * `applyColumnState` after it has ingested the column defs, so unmounting would deadlock the very
 * apply this hold is waiting for.
 *
 * Three ways out, all of them terminal — this is the whole point of the hook. `markApplied()` is the
 * normal one. `viewsFailed` is the one that bit: the apply effect bails on `!views.data`, so a failed
 * views query never reaches its reveal and the grid would stay blank forever. The timeout covers
 * everything else.
 */
export function useFirstFrameHold(viewsFailed: boolean): [boolean, () => void] {
  const [settled, setSettled] = useState(false);
  const markApplied = useCallback(() => setSettled(true), []);
  useEffect(() => {
    if (settled) return;
    if (viewsFailed) {
      setSettled(true);
      return;
    }
    const t = setTimeout(() => setSettled(true), FIRST_FRAME_HOLD_MS);
    return () => clearTimeout(t);
  }, [settled, viewsFailed]);
  return [!settled, markApplied];
}
